import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Globe, Search, ShoppingCart, Clock, CheckCircle, XCircle,
  RefreshCw, ChevronDown, ChevronRight, ChevronUp, Users, Wallet, Settings2,
  AlertTriangle, AlertCircle, X, Mail, Tag, Save, Eye, Shield, Lock,
} from 'lucide-react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { DomainRequestModal } from '@/components/DomainRequestModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OPS = ['add', 'renew', 'transfer', 'restore'];
const OP_LABELS = { add: 'ADD', renew: 'RENEW', transfer: 'TRANSFER', restore: 'RESTORE' };
const OP_COLORS = { add: 'text-blue-600', renew: 'text-green-600', transfer: 'text-violet-600', restore: 'text-amber-600' };
const OP_BORDER = { add: 'border-blue-100', renew: 'border-green-100', transfer: 'border-violet-100', restore: 'border-amber-100' };

function fmt(n) {
  return '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}
function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}
function calcPrice(adminPrice, markup, profitPct) {
  const a = parseFloat(adminPrice || 0);
  const m = parseFloat(markup || 0);
  const p = parseFloat(profitPct || 0);
  return Math.round((a + m) * (1 + p / 100) * 100) / 100;
}

export default function Domains() {
  const { user } = useAuth();
  const isCP = user?.role === 'sub_reseller';

  const [activeTab, setActiveTab]   = useState('register');
  const [tlds, setTlds]             = useState([]);
  const [partnerPricing, setPartnerPricing] = useState({});
  const [costPricing, setCostPricing]       = useState({});   // effective cost price per TLD/year
  const [clients, setClients]       = useState([]);
  const [orders, setOrders]         = useState([]);
  const [walletBalance, setWalletBalance] = useState(0);

  // Register tab state
  const [searchLabel, setSearchLabel]     = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching]         = useState(false);
  const [selectedClient, setSelectedClient] = useState('');
  const [years, setYears]                 = useState(1);
  const [buyingDomain, setBuyingDomain]   = useState('');
  const [showTldCatalog, setShowTldCatalog] = useState(true);
  const [catalogFilter, setCatalogFilter]   = useState('');

  // Expand / edit state
  const [expandedTld, setExpandedTld]     = useState(null);
  const [expandedView, setExpandedView]   = useState('operations');
  const [editingTld, setEditingTld]       = useState(null);
  const [editForm, setEditForm]           = useState({});
  const [savingTld, setSavingTld]         = useState('');

  // Bulk profit
  const [bulkProfit, setBulkProfit] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);

  // Domain Requests (approval)
  const [domainRequests, setDomainRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(null); // {id, domain}
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingId, setRejectingId] = useState('');

  // Orders
  const [ordersLoading, setOrdersLoading]     = useState(false);
  const [renewingOrderId, setRenewingOrderId] = useState('');
  const [renewYears, setRenewYears]           = useState(1);

  // Expiry alerts
  const [expiryAlerts, setExpiryAlerts] = useState([]);
  const [showExpiryAlert, setShowExpiryAlert] = useState(true);

  // Domain Request Modal
  const [modalOpen, setModalOpen]           = useState(false);
  const [pendingDomain, setPendingDomain]   = useState('');
  const [pendingPrice, setPendingPrice]     = useState(0);
  const [modalIsEmailOnly, setModalIsEmailOnly] = useState(false);
  const [modalSubmitting, setModalSubmitting]   = useState(false);

  // Email Pricing (partner custom pricing)
  const [emailPricing, setEmailPricing]       = useState(null);
  const [emailPricingForm, setEmailPricingForm] = useState(null);
  const [emailPricingLoading, setEmailPricingLoading] = useState(false);
  const [savingEmailPricing, setSavingEmailPricing]   = useState(false);

  // ── Fetchers ─────────────────────────────────────────────────────────────────
  const fetchTlds = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/domain-pricing/public`);
      setTlds(res.data || []);
    } catch { /* silent */ }
  }, []);

  const fetchPartnerPricing = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/partner/domain-pricing`);
      setPartnerPricing(res.data || {});
    } catch { /* silent */ }
  }, []);

  // Effective cost price per TLD/year (admin price for SP, SP's price for CP)
  const fetchCostPricing = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/partner/effective-domain-cost`);
      setCostPricing(res.data || {});
    } catch { /* silent */ }
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const endpoint = isCP ? '/sub-reseller/clients' : '/reseller/all-clients';
      const res = await axios.get(`${API}${endpoint}`);
      setClients(res.data || []);
    } catch { /* silent */ }
  }, [isCP]);

  const fetchOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const res = await axios.get(`${API}/partner/domain-orders`);
      setOrders(res.data?.orders || []);
    } catch { toast.error('Failed to load domain orders'); }
    finally { setOrdersLoading(false); }
  }, []);

  const fetchWallet = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/wallet/balance`);
      setWalletBalance(res.data?.balance || 0);
    } catch { /* silent */ }
  }, []);

  const fetchExpiryAlerts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/partner/domain-expiry-alerts`);
      setExpiryAlerts(res.data?.alerts || []);
    } catch { /* silent */ }
  }, []);

  const fetchEmailPricing = useCallback(async () => {
    setEmailPricingLoading(true);
    try {
      const res = await axios.get(`${API}/partner/email-pricing`);
      setEmailPricing(res.data);
      setEmailPricingForm({
        selling_price:          res.data.selling_price,
        original_price:         res.data.original_price,
        whois_privacy_price:    res.data.whois_privacy_price,
        domain_protector_price: res.data.domain_protector_price,
      });
    } catch { /* silent */ }
    finally { setEmailPricingLoading(false); }
  }, []);

  useEffect(() => {
    fetchTlds();
    fetchPartnerPricing();
    fetchCostPricing();
    fetchClients();
    fetchWallet();
    fetchExpiryAlerts();
    fetchEmailPricing();
  }, [fetchTlds, fetchPartnerPricing, fetchCostPricing, fetchClients, fetchWallet, fetchExpiryAlerts, fetchEmailPricing]);

  useEffect(() => {
    if (activeTab === 'orders') fetchOrders();
  }, [activeTab, fetchOrders]);

  // ── Edit helpers ─────────────────────────────────────────────────────────────
  function buildEditForm(tld) {
    const ep = partnerPricing[tld] || {};
    const tldDoc = tlds.find(t => t.tld === tld);
    const adminYearly = tldDoc?.yearly_pricing || [];
    const partnerYearly = ep.yearly_pricing || [];

    const yearly = adminYearly.map(adminRow => {
      const pr = partnerYearly.find(r => r.year === adminRow.year);
      return {
        year:        adminRow.year,
        admin_price: adminRow.selling_price,
        markup_cost: pr?.markup_cost ?? (ep.markup_add ?? ep.markup ?? 0),
        profit_pct:  pr?.profit_pct  ?? (ep.profit_pct_add ?? ep.profit_pct ?? 0),
      };
    });

    return {
      markup_add:          ep.markup_add          ?? ep.markup            ?? 0,
      profit_pct_add:      ep.profit_pct_add      ?? ep.profit_pct        ?? 0,
      markup_renew:        ep.markup_renew        ?? 0,
      profit_pct_renew:    ep.profit_pct_renew    ?? ep.profit_pct        ?? 0,
      markup_transfer:     ep.markup_transfer     ?? ep.transfer_markup   ?? 0,
      profit_pct_transfer: ep.profit_pct_transfer ?? ep.profit_pct        ?? 0,
      markup_restore:      ep.markup_restore      ?? 0,
      profit_pct_restore:  ep.profit_pct_restore  ?? ep.profit_pct        ?? 0,
      yearly,
    };
  }

  const startEdit = (tld) => {
    setEditingTld(tld);
    setEditForm(buildEditForm(tld));
  };

  const cancelEdit = () => {
    setEditingTld(null);
    setEditForm({});
  };

  const setOpField = (op, field, val) =>
    setEditForm(p => ({ ...p, [`${field}_${op}`]: val }));

  const setYearlyField = (i, field, val) => {
    const yearly = [...editForm.yearly];
    yearly[i] = { ...yearly[i], [field]: val };
    setEditForm(p => ({ ...p, yearly }));
  };

  const handleSaveTld = async (tld) => {
    setSavingTld(tld);
    try {
      await axios.post(`${API}/partner/domain-pricing`, {
        tld_settings: [{
          tld,
          markup_add:          parseFloat(editForm.markup_add)          || 0,
          profit_pct_add:      parseFloat(editForm.profit_pct_add)      || 0,
          markup_renew:        parseFloat(editForm.markup_renew)        || 0,
          profit_pct_renew:    parseFloat(editForm.profit_pct_renew)    || 0,
          markup_transfer:     parseFloat(editForm.markup_transfer)     || 0,
          profit_pct_transfer: parseFloat(editForm.profit_pct_transfer) || 0,
          markup_restore:      parseFloat(editForm.markup_restore)      || 0,
          profit_pct_restore:  parseFloat(editForm.profit_pct_restore)  || 0,
          yearly_pricing: (editForm.yearly || []).map(r => ({
            year:        r.year,
            markup_cost: parseFloat(r.markup_cost) || 0,
            profit_pct:  parseFloat(r.profit_pct)  || 0,
          })),
        }],
      });
      toast.success(`.${tld} pricing saved!`);
      cancelEdit();
      setExpandedTld(null);
      await fetchPartnerPricing();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSavingTld('');
    }
  };

  // Bulk profit % — applies to all operations + all years for every TLD
  const applyBulkProfit = async () => {
    const pct = parseFloat(bulkProfit);
    if (isNaN(pct) || pct < 0) { toast.error('Valid profit % daalo'); return; }
    setBulkSaving(true);
    try {
      const tld_settings = tlds.map(t => {
        const ep = partnerPricing[t.tld] || {};
        const yearly = (t.yearly_pricing || []).map(adminRow => {
          const pr = (ep.yearly_pricing || []).find(r => r.year === adminRow.year);
          return { year: adminRow.year, markup_cost: pr?.markup_cost ?? 0, profit_pct: pct };
        });
        return {
          tld:                 t.tld,
          markup_add:          ep.markup_add          ?? ep.markup           ?? 0,
          profit_pct_add:      pct,
          markup_renew:        ep.markup_renew        ?? 0,
          profit_pct_renew:    pct,
          markup_transfer:     ep.markup_transfer     ?? ep.transfer_markup  ?? 0,
          profit_pct_transfer: pct,
          markup_restore:      ep.markup_restore      ?? 0,
          profit_pct_restore:  pct,
          yearly_pricing:      yearly,
        };
      });
      await axios.post(`${API}/partner/domain-pricing`, { tld_settings });
      toast.success(`Profit % ${pct}% sabhi TLDs ke liye set!`);
      setBulkProfit('');
      await fetchPartnerPricing();
    } catch { toast.error('Bulk update failed'); }
    finally { setBulkSaving(false); }
  };

  // ── Domain search & buy ───────────────────────────────────────────────────────
  const handleSearch = async (labelOverride) => {
    const label = (labelOverride || searchLabel).trim().toLowerCase().split('.')[0];
    if (!label) { toast.error('Domain label daalo (e.g. "mycompany")'); return; }
    setSearching(true);
    setSearchResults([]);
    try {
      const res = await axios.get(`${API}/white-label/domain/search`, { params: { name: label } });
      setSearchResults(res.data?.domains || []);
      if (!res.data?.domains?.length) toast.info('No results. Try a different name.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Domain search failed. Works on live production server.');
    } finally {
      setSearching(false);
    }
  };

  const fetchRequests = useCallback(async () => {
    setRequestsLoading(true);
    try {
      const res = await axios.get(`${API}/domain/requests`);
      setDomainRequests(res.data || []);
    } catch { /* silent */ }
    finally { setRequestsLoading(false); }
  }, []);

  const handleRequest = (domain, price = 0) => {
    setPendingDomain(domain);
    setPendingPrice(price);
    setModalIsEmailOnly(false);
    setModalOpen(true);
  };

  const handleEmailOnlyRequest = (domain) => {
    setPendingDomain(domain);
    setPendingPrice(0);
    setModalIsEmailOnly(true);
    setModalOpen(true);
  };

  const handleModalSubmit = async (payload) => {
    setModalSubmitting(true);
    try {
      const body = { ...payload };
      // for_client_id is already in payload (set by modal when forClientId prop is non-empty)
      // Also keep legacy client_id for backward compat if selectedClient is set but for_client_id not in payload
      if (selectedClient && !body.for_client_id) body.for_client_id = selectedClient;
      const res = await axios.post(`${API}/domain/request`, body);
      toast.success(res.data.message || `Request submitted for ${payload.domain}!`);
      setModalOpen(false);
      if (!payload.email_only) {
        setSearchResults(prev => prev.filter(d => d.domainName !== payload.domain));
        setActiveTab('requests');
      }
      fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Request submission failed');
    } finally {
      setModalSubmitting(false);
    }
  };

  const handleApproveRequest = async (reqId) => {
    try {
      const res = await axios.post(`${API}/domain/requests/${reqId}/approve`, {});
      toast.success(res.data.message || 'Request approved');
      fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approve failed');
    }
  };

  const handleRejectRequest = async () => {
    if (!rejectDialog) return;
    setRejectingId(rejectDialog.id);
    try {
      await axios.post(`${API}/domain/requests/${rejectDialog.id}/reject`, { reason: rejectReason });
      toast.success('Request rejected');
      setRejectDialog(null);
      setRejectReason('');
      fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reject failed');
    } finally {
      setRejectingId('');
    }
  };

  const handleBuy = (domain) => {
    const tldKey  = domain.includes('.') ? domain.split('.').slice(1).join('.') : domain;
    const tldDoc  = tlds.find(t => t.tld === tldKey);
    const adminAddP = adminPriceForYears(tldDoc, years);

    let displayPrice;
    if (selectedClient) {
      // Buying for a client/CP → show OWN markup price (what partner charges their customers)
      const ep = partnerPricing[tldKey] || {};
      const markup    = ep.markup_add    ?? ep.markup    ?? 0;
      const profitPct = ep.profit_pct_add ?? ep.profit_pct ?? 0;
      displayPrice = calcPrice(adminAddP, markup, profitPct);
    } else {
      // Buying for self → show cost price (admin price for SP, SP's price for CP)
      displayPrice = costPricing[tldKey]?.yearly?.[years] ?? adminAddP;
    }
    handleRequest(domain, displayPrice);
  };

  const handleSaveEmailPricing = async () => {
    if (!emailPricingForm) return;
    setSavingEmailPricing(true);
    try {
      const res = await axios.put(`${API}/partner/email-pricing`, {
        selling_price:          parseFloat(emailPricingForm.selling_price),
        original_price:         parseFloat(emailPricingForm.original_price),
        whois_privacy_price:    parseFloat(emailPricingForm.whois_privacy_price),
        domain_protector_price: parseFloat(emailPricingForm.domain_protector_price),
      });
      setEmailPricing(res.data);
      setEmailPricingForm({
        selling_price:          res.data.selling_price,
        original_price:         res.data.original_price,
        whois_privacy_price:    res.data.whois_privacy_price,
        domain_protector_price: res.data.domain_protector_price,
      });
      toast.success('Pricing saved! Your clients will see your custom pricing.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save pricing');
    } finally {
      setSavingEmailPricing(false);
    }
  };

  const handleRenew = async (order) => {
    setRenewingOrderId(order.id);
    try {
      const res = await axios.post(`${API}/white-label/domain/renew`, {
        domain_order_id: order.id,
        years: renewYears,
      });
      if (res.data?.status === 'insufficient_balance') {
        toast.error(`Insufficient balance! Need ${fmt(res.data.required)}, have ${fmt(res.data.balance)}.`);
        return;
      }
      toast.success(res.data.message || `${order.domain} renewed!`);
      setWalletBalance(res.data.new_wallet_balance ?? walletBalance);
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Domain renewal failed');
    } finally {
      setRenewingOrderId('');
    }
  };

  function adminPriceForYears(tldObj, yr) {
    const yearly = tldObj?.yearly_pricing || [];
    const row = yearly.find(y => y.year === yr);
    return row ? row.selling_price : (tldObj?.selling_price?.add || 0) * yr;
  }

  const filteredTlds = tlds.filter(t =>
    !catalogFilter || t.tld.toLowerCase().includes(catalogFilter.toLowerCase())
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout role={user?.role}>
      <div className="space-y-6">

        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Globe className="w-6 h-6 text-blue-600" />
              Domain Registration
            </h1>
            <p className="text-slate-500 mt-1 text-sm">Set your markup &amp; sell domains to clients</p>
          </div>
          {!isCP && (
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
              <Wallet className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-blue-700 font-medium">Wallet: {fmt(walletBalance)}</span>
            </div>
          )}
        </div>

        {/* Expiry Alert Banner */}
        {expiryAlerts.length > 0 && showExpiryAlert && (
          <div
            data-testid="expiry-alert-banner"
            className={`rounded-xl border px-4 py-3 flex flex-col gap-2 ${
              expiryAlerts.some(a => a.status === 'expired' || a.status === 'critical')
                ? 'bg-red-50 border-red-200'
                : 'bg-amber-50 border-amber-200'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {expiryAlerts.some(a => a.status === 'expired' || a.status === 'critical')
                  ? <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  : <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />}
                <span className="text-sm font-semibold text-slate-800">
                  {expiryAlerts.filter(a => a.status === 'expired').length > 0
                    ? `${expiryAlerts.filter(a => a.status === 'expired').length} domain(s) expired!`
                    : `${expiryAlerts.length} domain(s) expiring within 30 days`}
                </span>
              </div>
              <button
                data-testid="dismiss-expiry-alert"
                onClick={() => setShowExpiryAlert(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {expiryAlerts.map(alert => (
                <span
                  key={alert.domain}
                  data-testid={`expiry-chip-${alert.domain}`}
                  className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium
                    ${alert.status === 'expired'
                      ? 'bg-red-100 text-red-700 border border-red-200'
                      : alert.status === 'critical'
                      ? 'bg-orange-100 text-orange-700 border border-orange-200'
                      : 'bg-amber-100 text-amber-700 border border-amber-200'}`}
                >
                  <span className="font-mono">{alert.domain}</span>
                  <span className="opacity-70">·</span>
                  {alert.status === 'expired'
                    ? 'Expired'
                    : alert.days_left === 0
                    ? 'Expires today'
                    : `${alert.days_left}d left`}
                  {alert.client_name && (
                    <span className="opacity-60">({alert.client_name})</span>
                  )}
                </span>
              ))}
            </div>
            <p className="text-xs text-slate-500">
              Renew from the <button className="text-blue-600 underline" onClick={() => setActiveTab('orders')}>My Domain Orders</button> tab.
            </p>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-slate-200">
          <div className="flex gap-1 overflow-x-auto">
            {[
              { key: 'register',       label: 'Request Domain',         icon: Search },
              { key: 'requests',       label: 'Domain Requests',         icon: Clock },
              { key: 'orders',         label: 'Registered Domains',      icon: CheckCircle },
              { key: 'available-tlds', label: `Available TLDs & Pricing (${tlds.length})`, icon: Tag },
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                data-testid={`tab-${key}`}
                onClick={() => { setActiveTab(key); if (key === 'requests') fetchRequests(); }}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
                  ${activeTab === key
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`}
              >
                <Icon className="w-4 h-4" />
                {label}
                {key === 'requests' && domainRequests.filter(r =>
                  (r.status === 'pending_sp' && r.sp_id === user?.id) ||
                  (r.status === 'pending_cp' && r.cp_id === user?.id)
                ).length > 0 && (
                  <span className="bg-amber-100 text-amber-700 text-xs px-1.5 py-0.5 rounded-full">
                    {domainRequests.filter(r =>
                      (r.status === 'pending_sp' && r.sp_id === user?.id) ||
                      (r.status === 'pending_cp' && r.cp_id === user?.id)
                    ).length}
                  </span>
                )}
                {key === 'orders' && orders.length > 0 && (
                  <span className="bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5 rounded-full">
                    {orders.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ══ REGISTER DOMAIN TAB ══ */}
        {activeTab === 'register' && (
          <div className="space-y-5">

            {/* Search bar */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h2 className="text-sm font-semibold text-slate-700 mb-4">Check Domain Availability</h2>
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  data-testid="domain-search-input"
                  placeholder="Enter domain name (e.g. mycompany)"
                  value={searchLabel}
                  onChange={e => setSearchLabel(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  className="flex-1"
                />
                <Button
                  data-testid="domain-search-btn"
                  onClick={() => handleSearch()}
                  disabled={searching}
                  className="bg-blue-600 hover:bg-blue-700 text-white gap-2 whitespace-nowrap"
                >
                  {searching ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  {searching ? 'Searching…' : 'Check Availability'}
                </Button>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mt-4">
                <div className="flex-1">
                  <label className="text-xs text-slate-500 mb-1 block flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" /> Register for Client (optional)
                  </label>
                  <select
                    data-testid="client-select"
                    value={selectedClient}
                    onChange={e => setSelectedClient(e.target.value)}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">— Own Use —</option>
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>{c.company_name || c.email}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:w-40">
                  <label className="text-xs text-slate-500 mb-1 block">Years</label>
                  <select
                    data-testid="years-select"
                    value={years}
                    onChange={e => setYears(parseInt(e.target.value))}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {[1,2,3,4,5,6,7,8,9,10].map(y => (
                      <option key={y} value={y}>{y} Year{y > 1 ? 's' : ''}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Search results */}
            {searchResults.length > 0 && (              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
                  <h2 className="text-sm font-semibold text-slate-700">
                    Results for "{searchLabel.trim()}"
                  </h2>
                </div>
                <div className="divide-y divide-slate-100">
                  {searchResults.map(d => {
                    const tldKey = d.domainName.split('.').slice(1).join('.');
                    const tldDoc = tlds.find(t => t.tld === tldKey);
                    const ep     = partnerPricing[tldKey] || {};
                    const adminAddP = adminPriceForYears(tldDoc, years);
                    const markup = ep.markup_add ?? ep.markup ?? 0;
                    const profitPct = ep.profit_pct_add ?? ep.profit_pct ?? 0;
                    const price = calcPrice(adminAddP, markup, profitPct);
                    return (
                      <div
                        key={d.domainName}
                        data-testid={`domain-result-${d.domainName}`}
                        className="flex items-center justify-between px-5 py-4 hover:bg-slate-50"
                      >
                        <div className="flex items-center gap-3">
                          {d.available
                            ? <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                            : <XCircle    className="w-5 h-5 text-red-400 flex-shrink-0" />}
                          <div>
                            <span className="font-medium text-slate-800">{d.domainName}</span>
                            <span className={`ml-2 text-xs px-2 py-0.5 rounded-full font-medium
                              ${d.available ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-500'}`}>
                              {d.available ? 'Available' : 'Taken'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="text-sm font-semibold text-slate-800">{fmt(price)}</div>
                            {price !== adminAddP && (
                              <div className="text-xs text-slate-400">Base: {fmt(adminAddP)}</div>
                            )}
                          </div>
                          {d.available && (
                            <Button
                              data-testid={`buy-btn-${d.domainName}`}
                              size="sm"
                              onClick={() => handleBuy(d.domainName)}
                              className="bg-green-600 hover:bg-green-700 text-white gap-1"
                            >
                              <ShoppingCart className="w-3.5 h-3.5" />
                              Request {years}yr
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          </div>
        )}

        {/* ══ AVAILABLE TLDs & PRICING TAB ══ */}
        {activeTab === 'available-tlds' && (
          <div className="space-y-5">

            {/* Email Pricing Settings Card */}
            {emailPricing?.allow_partner_email_pricing && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-6">
                <div className="flex items-center gap-2 mb-1">
                  <Mail className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-semibold text-slate-800">Your Pricing Settings</span>
                  {emailPricing?.has_custom_pricing && (
                    <span className="text-[10px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full uppercase">Custom</span>
                  )}
                </div>

                {emailPricingLoading || !emailPricingForm ? (
                  <div className="flex items-center gap-2 text-slate-400 text-sm">
                    <RefreshCw className="w-4 h-4 animate-spin" /> Loading...
                  </div>
                ) : (
                  <>
                    {/* ── Email Hosting Pricing ── */}
                    <div>
                      <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5 text-violet-500" /> Email Hosting Pricing
                      </p>
                      <p className="text-xs text-slate-400 mb-3">
                        CloudSetu base price: <strong>₹{emailPricing?.base_price}/account/year</strong>
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
                        <div>
                          <label className="text-xs font-semibold text-slate-600 block mb-1.5">Your Selling Price (₹/account/year)</label>
                          <Input
                            type="number" min="0" step="1"
                            value={emailPricingForm.selling_price}
                            onChange={e => setEmailPricingForm(f => ({ ...f, selling_price: e.target.value }))}
                            className="h-9 text-sm"
                            data-testid="partner-email-selling-price"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-slate-600 block mb-1.5">Original / Crossed-Out Price (₹/account/year)</label>
                          <Input
                            type="number" min="0" step="1"
                            value={emailPricingForm.original_price}
                            onChange={e => setEmailPricingForm(f => ({ ...f, original_price: e.target.value }))}
                            className="h-9 text-sm"
                            data-testid="partner-email-original-price"
                          />
                        </div>
                        <div className="bg-green-50 border border-green-100 rounded-xl px-3 py-2 text-xs flex items-center gap-2 h-9 self-end">
                          <span className="text-slate-400 line-through">₹{emailPricingForm.original_price}</span>
                          <span className="font-bold text-green-700">₹{emailPricingForm.selling_price}/acc/yr</span>
                        </div>
                      </div>
                    </div>

                    {/* ── WHOIS Privacy Pricing ── */}
                    <div className="border-t border-slate-100 pt-4">
                      <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                        <Shield className="w-3.5 h-3.5 text-slate-500" /> WHOIS Privacy Pricing
                      </p>
                      <p className="text-xs text-slate-400 mb-3">
                        CloudSetu base price: <strong>₹{emailPricing?.base_whois_price}/year</strong>
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
                        <div>
                          <label className="text-xs font-semibold text-slate-600 block mb-1.5">Your Selling Price (₹/year)</label>
                          <Input
                            type="number" min="0" step="1"
                            value={emailPricingForm.whois_privacy_price ?? emailPricing.base_whois_price}
                            onChange={e => setEmailPricingForm(f => ({ ...f, whois_privacy_price: e.target.value }))}
                            className="h-9 text-sm"
                            data-testid="partner-whois-selling-price"
                          />
                        </div>
                        <div className="bg-blue-50 border border-blue-100 rounded-xl px-3 py-2 text-xs flex items-center gap-2 h-9 self-end">
                          <Shield className="w-3.5 h-3.5 text-blue-500" />
                          <span className="font-semibold text-blue-700">+₹{emailPricingForm.whois_privacy_price ?? emailPricing.base_whois_price}/year</span>
                        </div>
                      </div>
                    </div>

                    {/* ── Domain Protector+ Pricing ── */}
                    {emailPricing?.allow_partner_domain_protector_pricing && (
                      <div className="border-t border-slate-100 pt-4">
                        <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                          <Lock className="w-3.5 h-3.5 text-blue-600" /> Domain Protector+ Pricing
                        </p>
                        <p className="text-xs text-slate-400 mb-3">
                          CloudSetu base price: <strong>₹{emailPricing?.base_domain_protector_price}/year</strong>
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
                          <div>
                            <label className="text-xs font-semibold text-slate-600 block mb-1.5">Your Selling Price (₹/year)</label>
                            <Input
                              type="number" min="0" step="1"
                              value={emailPricingForm.domain_protector_price ?? emailPricing.base_domain_protector_price}
                              onChange={e => setEmailPricingForm(f => ({ ...f, domain_protector_price: e.target.value }))}
                              className="h-9 text-sm"
                              data-testid="partner-dp-selling-price"
                            />
                          </div>
                          <div className="bg-blue-50 border border-blue-100 rounded-xl px-3 py-2 text-xs flex items-center gap-2 h-9 self-end">
                            <Lock className="w-3.5 h-3.5 text-blue-600" />
                            <span className="font-semibold text-blue-700">+₹{emailPricingForm.domain_protector_price ?? emailPricing.base_domain_protector_price}/year</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Save Button */}
                    <div className="flex justify-end pt-2">
                      <Button
                        size="sm"
                        onClick={handleSaveEmailPricing}
                        disabled={savingEmailPricing}
                        className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5 h-9"
                        data-testid="save-email-pricing-btn"
                      >
                        <Save className="w-3.5 h-3.5" />
                        {savingEmailPricing ? 'Saving…' : 'Save All Pricing'}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* TLD Catalog */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              {/* Catalog header */}
              <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 bg-slate-50 border-b border-slate-200">
                <span className="text-sm font-semibold text-slate-700">
                  Available TLDs &amp; Pricing ({filteredTlds.length})
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 whitespace-nowrap">Set all Profit %</span>
                  <Input
                    data-testid="bulk-profit-input"
                    type="number" min="0" step="0.5"
                    placeholder="e.g. 10"
                    value={bulkProfit}
                    onChange={e => setBulkProfit(e.target.value)}
                    className="w-20 h-8 text-sm px-2"
                  />
                  <Button
                    data-testid="bulk-profit-apply-btn"
                    size="sm" variant="outline"
                    onClick={applyBulkProfit}
                    disabled={bulkSaving || !bulkProfit}
                    className="h-8 text-xs px-3"
                  >
                    {bulkSaving ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Apply'}
                  </Button>
                </div>
              </div>

              {/* Filter */}
              <div className="px-5 py-2 border-b border-slate-100">
                <Input
                  placeholder="Filter TLDs (e.g. com, in)"
                  value={catalogFilter}
                  onChange={e => setCatalogFilter(e.target.value)}
                  className="max-w-xs h-8 text-sm"
                />
              </div>

              {/* TLD rows */}
              <div>
                {/* Table header */}
                <div
                  className="grid text-xs font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 px-4 py-2.5 border-b border-slate-100"
                  style={{ gridTemplateColumns: '28px 1fr 120px 120px 120px 140px 90px' }}
                >
                  <span></span>
                  <span>TLD</span>
                  <span className="text-right">Add (1year)</span>
                  <span className="text-right">Renew (1year)</span>
                  <span className="text-right text-blue-600">Selling Price</span>
                  <span className="text-center">Action</span>
                </div>

                    {filteredTlds.map(t => {
                      const isExpanded = expandedTld === t.tld;
                      const isEditing  = editingTld === t.tld;
                      const ep = partnerPricing[t.tld] || {};
                      const adminAddP      = t.selling_price?.add      || 0;
                      const adminRenewP    = t.selling_price?.renew    || 0;
                      const adminTransferP = t.selling_price?.transfer || 0;
                      const markupAdd  = ep.markup_add  ?? ep.markup    ?? 0;
                      const profitAdd  = ep.profit_pct_add ?? ep.profit_pct ?? 0;
                      const yourAddP   = calcPrice(adminAddP, markupAdd, profitAdd);
                      const profitRs   = yourAddP - adminAddP;

                      return (
                        <div key={t.tld} className="border-b border-slate-50 last:border-0">
                          {/* Summary row */}
                          <div
                            className={`grid items-center px-4 py-3 hover:bg-slate-50 transition-colors text-sm cursor-pointer
                              ${isExpanded ? 'bg-blue-50/50' : ''}`}
                            style={{ gridTemplateColumns: '28px 1fr 120px 120px 120px 140px 90px' }}
                            data-testid={`tld-row-${t.tld}`}
                            onClick={() => {
                              if (isExpanded) {
                                setExpandedTld(null);
                                if (isEditing) cancelEdit();
                              } else {
                                setExpandedTld(t.tld);
                                setExpandedView('operations');
                              }
                            }}
                          >
                            <span className="text-slate-400">
                              {isExpanded
                                ? <ChevronDown className="w-4 h-4 text-blue-500" />
                                : <ChevronRight className="w-4 h-4" />}
                            </span>
                            <div>
                              <span className="font-mono font-semibold text-slate-800">.{t.tld}</span>
                              {t.popular && (
                                <span className="ml-1.5 text-[9px] font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
                                  HOT
                                </span>
                              )}
                            </div>
                            <div className="text-right font-medium text-slate-600">{fmt(adminAddP)}</div>
                            <div className="text-right text-slate-500 text-xs">{fmt(adminRenewP)}</div>
                            <div className="text-right font-semibold text-blue-700">{fmt(yourAddP)}</div>
                            <div className="text-center" onClick={e => e.stopPropagation()}>
                              <button
                                data-testid={`edit-tld-${t.tld}`}
                                onClick={() => {
                                  if (!isExpanded) setExpandedTld(t.tld);
                                  setExpandedView('operations');
                                  startEdit(t.tld);
                                }}
                                className="inline-flex items-center gap-1 text-xs border border-slate-300 text-slate-600 rounded px-2 py-1 hover:bg-slate-100 transition-colors"
                              >
                                <Settings2 className="w-3 h-3" />
                                Edit
                              </button>
                            </div>
                          </div>

                          {/* Expanded detail */}
                          {isExpanded && (
                            <div className="bg-blue-50/60 border-t border-blue-100 px-6 py-5">
                              {/* View toggle + Cancel/Save */}
                              <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                                <div className="flex gap-1 bg-white border border-slate-200 p-0.5 rounded-lg shadow-sm">
                                  {[
                                    ['operations', 'Operations (ADD/RENEW/TRANSFER/RESTORE)'],
                                    ['yearly',     '1–10 Year Pricing'],
                                  ].map(([v, l]) => (
                                    <button
                                      key={v}
                                      data-testid={`view-tab-${v}-${t.tld}`}
                                      onClick={() => setExpandedView(v)}
                                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all
                                        ${expandedView === v
                                          ? 'bg-blue-600 text-white shadow-sm'
                                          : 'text-slate-600 hover:bg-slate-100'}`}
                                    >
                                      {l}
                                    </button>
                                  ))}
                                </div>

                                <div className="flex items-center gap-2">
                                  {isEditing ? (
                                    <>
                                      <Button
                                        size="sm" variant="outline"
                                        onClick={cancelEdit}
                                        className="h-8 text-xs"
                                        data-testid={`cancel-edit-${t.tld}`}
                                      >
                                        Cancel
                                      </Button>
                                      <Button
                                        size="sm"
                                        onClick={() => handleSaveTld(t.tld)}
                                        disabled={savingTld === t.tld}
                                        className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                                        data-testid={`save-tld-${t.tld}`}
                                      >
                                        {savingTld === t.tld
                                          ? <><RefreshCw className="w-3 h-3 animate-spin mr-1" />Saving…</>
                                          : 'Save Changes'}
                                      </Button>
                                    </>
                                  ) : (
                                    <Button
                                      size="sm" variant="outline"
                                      onClick={() => startEdit(t.tld)}
                                      className="h-8 text-xs gap-1"
                                      data-testid={`start-edit-${t.tld}`}
                                    >
                                      <Settings2 className="w-3.5 h-3.5" />
                                      Edit Prices
                                    </Button>
                                  )}
                                </div>
                              </div>

                              {/* ── Operations view ── */}
                              {expandedView === 'operations' && (
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                  {OPS.map(op => {
                                    const adminP = t.selling_price?.[op] || 0;
                                    const mk = isEditing
                                      ? (editForm[`markup_${op}`] ?? 0)
                                      : (ep[`markup_${op}`] ?? (op === 'transfer' ? (ep.transfer_markup ?? 0) : 0));
                                    const pp = isEditing
                                      ? (editForm[`profit_pct_${op}`] ?? 0)
                                      : (ep[`profit_pct_${op}`] ?? ep.profit_pct ?? 0);
                                    const sp = calcPrice(adminP, mk, pp);

                                    return (
                                      <div
                                        key={op}
                                        className={`bg-white rounded-xl border p-4 ${OP_BORDER[op]}`}
                                        data-testid={`op-card-${op}-${t.tld}`}
                                      >
                                        <p className={`text-xs font-bold uppercase tracking-wide mb-3 ${OP_COLORS[op]}`}>
                                          {OP_LABELS[op]}
                                        </p>

                                        {isEditing ? (
                                          <div className="space-y-2">
                                            <div>
                                              <label className="text-[10px] text-slate-400 font-medium">
                                                Cost Price (₹)
                                              </label>
                                              <div className="h-7 mt-0.5 px-2 flex items-center rounded bg-slate-100 text-xs text-slate-500">
                                                {fmt(adminP)}
                                              </div>
                                            </div>
                                            <div>
                                              <label className="text-[10px] text-slate-500">Markup Cost (₹)</label>
                                              <input
                                                data-testid={`markup-${op}-${t.tld}`}
                                                type="number" min="0" step="1"
                                                value={editForm[`markup_${op}`] ?? ''}
                                                onChange={e => setOpField(op, 'markup', e.target.value)}
                                                placeholder="0"
                                                className="mt-0.5 w-full h-7 border border-slate-300 rounded px-2 text-xs focus:border-blue-400 outline-none"
                                              />
                                            </div>
                                            <div>
                                              <label className="text-[10px] text-slate-500">Profit %</label>
                                              <input
                                                data-testid={`profit-${op}-${t.tld}`}
                                                type="number" min="0" step="0.5"
                                                value={editForm[`profit_pct_${op}`] ?? ''}
                                                onChange={e => setOpField(op, 'profit_pct', e.target.value)}
                                                placeholder="0"
                                                className="mt-0.5 w-full h-7 border border-slate-300 rounded px-2 text-xs focus:border-blue-400 outline-none"
                                              />
                                            </div>
                                            <div className="bg-green-50 border border-green-100 rounded-lg px-2.5 py-2">
                                              <p className="text-[10px] text-green-600 font-medium">Selling Price</p>
                                              <p className="text-sm font-bold text-green-700">
                                                {fmt(calcPrice(adminP, editForm[`markup_${op}`] ?? 0, editForm[`profit_pct_${op}`] ?? 0))}
                                              </p>
                                              <p className="text-[9px] text-green-500 mt-0.5">
                                                (Coast + Markup) × (1 + Profit%)
                                              </p>
                                            </div>
                                          </div>
                                        ) : (
                                          <div className="space-y-1.5">
                                            <div className="flex justify-between text-xs">
                                              <span className="text-slate-400">Cost Price</span>
                                              <span className="font-medium text-slate-500">{fmt(adminP)}</span>
                                            </div>
                                            <div className="flex justify-between text-xs">
                                              <span className="text-slate-500">Markup</span>
                                              <span className="font-medium">{fmt(mk)}</span>
                                            </div>
                                            <div className="flex justify-between text-xs">
                                              <span className="text-slate-500">Profit</span>
                                              <span className="font-medium text-green-600">{pp}%</span>
                                            </div>
                                            <div className="border-t border-slate-100 pt-1.5 flex justify-between text-xs">
                                              <span className="font-semibold text-slate-700">Selling</span>
                                              <span className="font-bold text-slate-800">{fmt(sp)}</span>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                              {/* ── Yearly pricing view ── */}
                              {expandedView === 'yearly' && (
                                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="bg-slate-50 border-b border-slate-100">
                                        <th className="px-4 py-2.5 text-left font-semibold text-slate-600">Years</th>
                                        <th className="px-4 py-2.5 text-right font-semibold text-slate-500">Cost Price (Read-only)</th>
                                        <th className="px-4 py-2.5 text-right font-semibold text-slate-600">Markup Cost (₹)</th>
                                        <th className="px-4 py-2.5 text-right font-semibold text-slate-600">Profit %</th>
                                        <th className="px-4 py-2.5 text-right font-semibold text-slate-700">Selling Price</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {isEditing ? (
                                        (editForm.yearly || []).map((row, i) => {
                                          const sp = calcPrice(row.admin_price, row.markup_cost, row.profit_pct);
                                          return (
                                            <tr key={row.year} className={`border-b border-slate-50 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}>
                                              <td className="px-4 py-2.5 font-semibold text-slate-700">
                                                {row.year} {row.year === 1 ? 'Year' : 'Years'}
                                              </td>
                                              <td className="px-4 py-2.5 text-right text-slate-400 font-mono">
                                                {fmt(row.admin_price)}
                                              </td>
                                              <td className="px-4 py-2.5 text-right">
                                                <input
                                                  data-testid={`yr-markup-${row.year}-${t.tld}`}
                                                  type="number" min="0"
                                                  value={row.markup_cost}
                                                  onChange={e => setYearlyField(i, 'markup_cost', e.target.value)}
                                                  className="w-24 text-right border border-slate-200 rounded px-2 py-1 focus:border-blue-400 outline-none"
                                                />
                                              </td>
                                              <td className="px-4 py-2.5 text-right">
                                                <input
                                                  data-testid={`yr-profit-${row.year}-${t.tld}`}
                                                  type="number" min="0" max="500"
                                                  value={row.profit_pct}
                                                  onChange={e => setYearlyField(i, 'profit_pct', e.target.value)}
                                                  className="w-16 text-right border border-slate-200 rounded px-2 py-1 focus:border-blue-400 outline-none"
                                                />
                                              </td>
                                              <td className="px-4 py-2.5 text-right font-bold text-slate-800">
                                                {fmt(sp)}
                                              </td>
                                            </tr>
                                          );
                                        })
                                      ) : (
                                        (t.yearly_pricing || []).map((adminRow, i) => {
                                          const pr = (ep.yearly_pricing || []).find(r => r.year === adminRow.year);
                                          const mk = pr?.markup_cost ?? (ep.markup_add ?? ep.markup ?? 0);
                                          const pp = pr?.profit_pct  ?? (ep.profit_pct_add ?? ep.profit_pct ?? 0);
                                          const sp = calcPrice(adminRow.selling_price, mk, pp);
                                          return (
                                            <tr key={adminRow.year} className={`border-b border-slate-50 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}>
                                              <td className="px-4 py-2.5 font-semibold text-slate-700">
                                                {adminRow.year} {adminRow.year === 1 ? 'Year' : 'Years'}
                                              </td>
                                              <td className="px-4 py-2.5 text-right text-slate-400 font-mono">
                                                {fmt(adminRow.selling_price)}
                                              </td>
                                              <td className="px-4 py-2.5 text-right">{fmt(mk)}</td>
                                              <td className="px-4 py-2.5 text-right text-green-600 font-medium">{pp}%</td>
                                              <td className="px-4 py-2.5 text-right font-bold text-slate-800">
                                                {fmt(sp)}
                                              </td>
                                            </tr>
                                          );
                                        })
                                      )}
                                    </tbody>
                                  </table>
                                  {isEditing && (
                                    <p className="px-4 py-2 text-[10px] text-slate-400 bg-slate-50 border-t border-slate-100">
                                      Selling Price = (Cost Price + Markup Cost) × (1 + Profit%)
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {filteredTlds.length === 0 && (
                      <div className="py-10 text-center text-slate-400 text-sm">No TLDs found</div>
                    )}
                  </div>
            </div>
          </div>
        )}

        {/* ══ DOMAIN REQUESTS TAB ══ */}        {activeTab === 'requests' && (
          <DomainRequestsPanel
            requests={domainRequests}
            loading={requestsLoading}
            user={user}
            onApprove={handleApproveRequest}
            onReject={(req) => { setRejectDialog(req); setRejectReason(''); }}
            onRefresh={fetchRequests}
            fmt={fmt}
          />
        )}

        {/* ══ MY ORDERS TAB ══ */}
        {activeTab === 'orders' && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50">
              <h2 className="text-sm font-semibold text-slate-700">My Domain Registrations</h2>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">Renew for:</span>
                  <select
                    data-testid="renew-years-select"
                    value={renewYears}
                    onChange={e => setRenewYears(parseInt(e.target.value))}
                    className="border border-slate-300 rounded px-2 py-1 text-xs bg-white"
                  >
                    {[1,2,3,4,5].map(y => (
                      <option key={y} value={y}>{y} yr</option>
                    ))}
                  </select>
                </div>
                <Button
                  variant="outline" size="sm"
                  onClick={fetchOrders}
                  disabled={ordersLoading}
                  data-testid="refresh-orders-btn"
                  className="gap-1 text-xs"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${ordersLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>

            {ordersLoading ? (
              <div className="flex items-center justify-center py-16">
                <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-16">
                <Globe className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">No domain registrations yet.</p>
                <p className="text-slate-400 text-xs mt-1">Go to "Register Domain" tab to get started.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-left">
                      {['#','Domain','Client','Yrs','Amount','Commission','Date','Expiry','Status','Renew'].map(h => (
                        <th key={h} className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {orders.map((o, idx) => {
                      // Calculate expiry date for display
                      let expiryStr = o.expires_at;
                      if (!expiryStr && o.created_at) {
                        try {
                          const d = new Date(o.created_at);
                          d.setDate(d.getDate() + (o.years || 1) * 365);
                          expiryStr = d.toISOString();
                        } catch { /* noop */ }
                      }
                      const daysLeft = expiryStr
                        ? Math.round((new Date(expiryStr) - new Date()) / (1000 * 60 * 60 * 24))
                        : null;
                      const isExpiring = daysLeft !== null && daysLeft <= 30;
                      const isExpired  = daysLeft !== null && daysLeft < 0;

                      return (
                      <tr key={o.id || idx} data-testid={`order-row-${idx}`}
                        className={`hover:bg-slate-50 transition-colors ${isExpired ? 'bg-red-50/40' : isExpiring ? 'bg-amber-50/40' : ''}`}>
                        <td className="px-5 py-3 text-slate-400 text-xs">{idx + 1}</td>
                        <td className="px-5 py-3">
                          <span className="font-medium text-slate-800">{o.domain}</span>
                          {o.rc_order_id && <p className="text-xs text-slate-400 mt-0.5">RC: {o.rc_order_id}</p>}
                        </td>
                        <td className="px-5 py-3">
                          {o.client_name || o.client_email
                            ? <span className="text-slate-700">{o.client_name || o.client_email}</span>
                            : <span className="text-slate-400 text-xs">Own Use</span>}
                        </td>
                        <td className="px-5 py-3 text-slate-600">{o.years}</td>
                        <td className="px-5 py-3 font-semibold text-slate-700">{fmt(o.selling_price)}</td>
                        <td className="px-5 py-3 text-green-600 font-medium">{fmt(o.commission)}</td>
                        <td className="px-5 py-3 text-slate-500 text-xs">{fmtDate(o.created_at)}</td>
                        <td className="px-5 py-3 text-xs whitespace-nowrap">
                          {expiryStr ? (
                            <div>
                              <span className={isExpired ? 'text-red-600 font-medium' : isExpiring ? 'text-amber-600 font-medium' : 'text-slate-500'}>
                                {fmtDate(expiryStr)}
                              </span>
                              {daysLeft !== null && (
                                <p className={`text-[10px] mt-0.5 ${
                                  isExpired ? 'text-red-500' :
                                  isExpiring && daysLeft <= 7 ? 'text-orange-500 font-semibold' :
                                  isExpiring ? 'text-amber-500' : 'text-slate-400'}`}>
                                  {isExpired
                                    ? `Expired ${Math.abs(daysLeft)}d ago`
                                    : daysLeft === 0
                                    ? 'Expires today'
                                    : `${daysLeft}d left`}
                                </p>
                              )}
                            </div>
                          ) : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-5 py-3">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium
                            ${o.status === 'active' || o.status === 'renewed' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                            {o.type === 'renewal' ? 'Renewal' : o.status || 'active'}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex flex-col gap-1.5">
                          {o.type !== 'renewal' && (
                            <Button
                              size="sm" variant="outline"
                              data-testid={`renew-btn-${idx}`}
                              disabled={renewingOrderId === o.id}
                              onClick={() => handleRenew(o)}
                              className={`text-xs gap-1 ${isExpiring || isExpired ? 'text-orange-600 border-orange-300 hover:bg-orange-50' : 'text-blue-600 border-blue-200 hover:bg-blue-50'}`}
                            >
                              {renewingOrderId === o.id
                                ? <RefreshCw className="w-3 h-3 animate-spin" />
                                : <RefreshCw className="w-3 h-3" />}
                              Renew
                            </Button>
                          )}
                          <Button
                            size="sm" variant="outline"
                            data-testid={`email-hosting-btn-${idx}`}
                            onClick={() => handleEmailOnlyRequest(o.domain)}
                            className="text-xs gap-1 text-violet-600 border-violet-200 hover:bg-violet-50"
                          >
                            <Mail className="w-3 h-3" />
                            + Email
                          </Button>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Reject Dialog */}
      {rejectDialog && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setRejectDialog(null)}>
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold text-slate-800 mb-2">Reject Request</h3>
            <p className="text-sm text-slate-500 mb-3">Domain: <strong>{rejectDialog.domain}</strong></p>
            <label className="text-xs font-medium text-slate-600 block mb-1">Reason (optional)</label>
            <textarea
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none min-h-[70px]"
              placeholder="Reason for rejection..."
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
            />
            <div className="flex gap-2 mt-4 justify-end">
              <button className="px-4 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50" onClick={() => setRejectDialog(null)}>Cancel</button>
              <button
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700 disabled:opacity-50"
                disabled={!!rejectingId}
                onClick={handleRejectRequest}
              >
                {rejectingId ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Domain Request Modal */}
      <DomainRequestModal
        open={modalOpen}
        onClose={() => !modalSubmitting && setModalOpen(false)}
        domain={pendingDomain}
        years={years}
        price={pendingPrice}
        isEmailOnly={modalIsEmailOnly}
        onSubmit={handleModalSubmit}
        submitting={modalSubmitting}
        forClientId={selectedClient}
        forClientName={
          selectedClient
            ? (clients.find(c => c.id === selectedClient)?.company_name ||
               clients.find(c => c.id === selectedClient)?.account_name ||
               clients.find(c => c.id === selectedClient)?.email || '')
            : ''
        }
        overrideSettings={
          selectedClient && emailPricing
            ? {
                email_price_per_account: emailPricing.selling_price    ?? emailPricing.email_price_per_account,
                email_original_price:    emailPricing.original_price   ?? emailPricing.email_original_price,
                whois_privacy_price:     emailPricing.whois_privacy_price,
                domain_protector_price:  emailPricing.domain_protector_price,
              }
            : null
        }
      />

    </DashboardLayout>
  );
}

/* ─── Request Details Modal ─── */
function RequestDetailsModal({ request: r, onClose, fmt }) {
  if (!r) return null;
  const isQuoted        = (r.quoted_total || 0) > 0;
  const domainBase      = isQuoted ? (r.quoted_domain_price || 0) : Math.max(0, (r.price || 0) - (r.whois_addon_price || 0));
  const whoisCost       = isQuoted ? (r.quoted_whois_cost || 0) : (r.whois_addon_price || 0);
  const emailCost       = isQuoted ? (r.quoted_email_cost || 0) : (r.email_cost_estimate || 0);
  const dpCost          = isQuoted ? (r.quoted_protector_cost || 0) : (r.domain_protector_cost || 0);
  const wholesaleTotal  = (r.price || 0) + (r.email_cost_estimate || 0) + (r.domain_protector_cost || 0);
  const breakdownTotal  = isQuoted ? r.quoted_total : wholesaleTotal;
  const totalAmount     = wholesaleTotal; // kept for any remaining references
  const hasEmail        = r.email_hosting_requested || r.email_only;

  const statusMap = {
    pending_sp:      { label: 'Pending Approval',    cls: 'bg-amber-100 text-amber-700' },
    pending_cp:      { label: 'Pending Approval',    cls: 'bg-amber-100 text-amber-700' },
    pending_admin:   { label: 'Pending Approval', cls: 'bg-blue-100 text-blue-700' },
    payment_pending: { label: 'Payment Pending',        cls: 'bg-red-100 text-red-600' },
    registered:      { label: 'Registered',             cls: 'bg-green-100 text-green-700' },
    rejected:        { label: 'Rejected',               cls: 'bg-red-100 text-red-600' },
  };
  const s = statusMap[r.status] || { label: r.status, cls: 'bg-slate-100 text-slate-600' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="request-details-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-base font-semibold text-slate-800 font-mono">{r.domain}</h2>
            <p className="text-xs text-slate-400 mt-0.5">Request Details</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600" data-testid="close-request-details">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Status + Meta */}
          <div className="flex flex-wrap gap-3 items-center">
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${s.cls}`}>{s.label}</span>
            <span className="text-xs text-slate-400">{r.years} year{r.years !== 1 ? 's' : ''}</span>
            {r.whois_privacy && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 flex items-center gap-1">
                <Shield className="w-3 h-3" /> WHOIS Privacy
              </span>
            )}
            {r.domain_lock && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 flex items-center gap-1">
                <Lock className="w-3 h-3" /> Domain Lock
              </span>
            )}
            {r.email_only && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 font-semibold">EMAIL ONLY</span>
            )}
          </div>

          {/* Requested by */}
          <div className="text-sm text-slate-600">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1">Requested By</span>
            <span className="font-medium">{r.requested_by_name || '—'}</span>
            {r.requested_by_role && <span className="ml-2 text-xs text-slate-400 capitalize">({r.requested_by_role.replace('_', ' ')})</span>}
          </div>

          {/* Pricing Breakdown */}
          {!r.email_only && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">{isQuoted ? 'Price Breakdown' : 'Price Breakdown (Wholesale Cost)'}</p>
              <div className="bg-slate-50 rounded-xl border border-slate-200 divide-y divide-slate-100">
                <div className="flex justify-between items-center px-4 py-2.5 text-sm">
                  <span className="text-slate-600">Domain Registration ({r.years}yr)</span>
                  <span className="font-medium text-slate-800">{fmt(domainBase)}</span>
                </div>
                {whoisCost > 0 && (
                  <div className="flex justify-between items-center px-4 py-2.5 text-sm">
                    <span className="text-slate-600 flex items-center gap-1"><Shield className="w-3.5 h-3.5 text-slate-400" /> WHOIS Privacy</span>
                    <span className="font-medium text-slate-800">{fmt(whoisCost)}</span>
                  </div>
                )}
                {emailCost > 0 && (
                  <div className="flex justify-between items-center px-4 py-2.5 text-sm">
                    <span className="text-slate-600 flex items-center gap-1"><Mail className="w-3.5 h-3.5 text-violet-500" /> Email Hosting ({r.email_accounts_count} acc × {r.years}yr)</span>
                    <span className="font-medium text-violet-700">{fmt(emailCost)}</span>
                  </div>
                )}
                {dpCost > 0 && (
                  <div className="flex justify-between items-center px-4 py-2.5 text-sm">
                    <span className="text-slate-600 flex items-center gap-1"><Lock className="w-3.5 h-3.5 text-blue-600" /> Domain Protector+</span>
                    <span className="font-medium text-blue-700">{fmt(dpCost)}</span>
                  </div>
                )}
                {isQuoted && (
                  <>
                    <div className="flex justify-between items-center px-4 py-2.5 text-sm">
                      <span className="text-slate-600">Subtotal (excl. GST)</span>
                      <span className="font-medium text-slate-800">{fmt(r.quoted_subtotal || 0)}</span>
                    </div>
                    <div className="flex justify-between items-center px-4 py-2.5 text-sm">
                      <span className="text-slate-600">GST (18% IGST)</span>
                      <span className="font-medium text-slate-800">{fmt(r.quoted_gst || 0)}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between items-center px-4 py-2.5 bg-slate-100 rounded-b-xl">
                  <span className="text-sm font-semibold text-slate-700">{isQuoted ? 'Total (incl. GST)' : 'Your Cost (Wholesale)'}</span>
                  <span className="text-sm font-bold text-slate-800">{fmt(breakdownTotal)}</span>
                </div>
              </div>

              {/* Client Pays + Profit (only if client_facing_total available) */}
              {r.client_facing_total > 0 && (
                <div className="mt-2 bg-green-50 rounded-xl border border-green-200 divide-y divide-green-100">
                  <div className="flex justify-between items-center px-4 py-2.5 text-sm">
                    <span className="text-slate-600 font-medium">Client Pays Total</span>
                    <span className="font-bold text-slate-800">{fmt(r.client_facing_total)}</span>
                  </div>
                  <div className="flex justify-between items-center px-4 py-2.5 bg-green-100 rounded-b-xl">
                    <span className="text-sm font-semibold text-green-800">Your Profit</span>
                    <span className="text-sm font-bold text-green-700">{fmt(Math.max(0, r.client_facing_total - wholesaleTotal))}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Email Hosting Details */}
          {hasEmail && (
            <div>
              <p className="text-xs font-semibold text-violet-600 uppercase tracking-wide mb-2 flex items-center gap-1">
                <Mail className="w-3.5 h-3.5" /> Email Hosting Details
              </p>
              <div className="bg-violet-50 rounded-xl border border-violet-100 px-4 py-3 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-slate-500 text-xs">Accounts:</span>
                  <span className="font-semibold text-slate-800">
                    {r.email_accounts_count > 0 ? `${r.email_accounts_count} account(s)` : 'Not specified'}
                  </span>
                </div>
                {r.email_usernames?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1.5">Usernames:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {r.email_usernames.map((u, i) => (
                        <span key={i} className="text-xs bg-white border border-violet-200 text-violet-700 rounded-full px-2.5 py-0.5 font-mono">
                          {u}@{r.domain}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {r.notes && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Notes</p>
              <div className="bg-slate-50 rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-600">{r.notes}</div>
            </div>
          )}

          {/* Rejection Reason */}
          {r.status === 'rejected' && r.rejection_reason && (
            <div>
              <p className="text-xs font-semibold text-red-500 uppercase tracking-wide mb-2">Rejection Reason</p>
              <div className="bg-red-50 rounded-xl border border-red-100 px-4 py-3 text-sm text-red-700">{r.rejection_reason}</div>
            </div>
          )}

          {/* Submission date */}
          <p className="text-xs text-slate-400 text-right">
            Submitted: {r.created_at ? new Date(r.created_at).toLocaleString('en-IN') : '—'}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ─── Domain Requests Panel Component ─── */
function DomainRequestsPanel({ requests, loading, user, onApprove, onReject, onRefresh, fmt }) {
  const [approvingId, setApprovingId] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [viewingRequest, setViewingRequest] = useState(null);
  const userId = user?.id || '';
  const userRole = user?.role || '';

  const handleApprove = async (reqId) => {
    setApprovingId(reqId);
    await onApprove(reqId);
    setApprovingId('');
  };

  const statusMap = {
    pending_sp:      { label: 'Pending SP Approval',    cls: 'bg-amber-100 text-amber-700' },
    pending_cp:      { label: 'Pending CP Approval',    cls: 'bg-amber-100 text-amber-700' },
    pending_admin:   { label: 'Pending Admin Approval', cls: 'bg-blue-100 text-blue-700' },
    payment_pending: { label: 'Payment Pending',        cls: 'bg-red-100 text-red-600' },
    registered:      { label: 'Registered',             cls: 'bg-green-100 text-green-700' },
    rejected:        { label: 'Rejected',               cls: 'bg-red-100 text-red-600' },
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50">
        <h2 className="text-sm font-semibold text-slate-700">Domain Requests</h2>
        <button onClick={onRefresh} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg px-3 py-1.5 bg-white hover:bg-slate-50">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {/* Request Details Modal */}
      {viewingRequest && (
        <RequestDetailsModal
          request={viewingRequest}
          onClose={() => setViewingRequest(null)}
          fmt={fmt}
        />
      )}

      {loading ? (
        <div className="flex justify-center py-14"><RefreshCw className="w-5 h-5 animate-spin text-slate-300" /></div>
      ) : requests.length === 0 ? (
        <div className="text-center py-14">
          <Globe className="w-10 h-10 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">No domain requests found</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left">
                {['', 'Domain', 'Requested By', 'Years', 'Total Price', 'Submitted', 'Status', 'Action'].map(h => (
                  <th key={h} className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {requests.map((r) => {
                const s = statusMap[r.status] || { label: r.status, cls: 'bg-slate-100 text-slate-600' };
                const canApprove = (
                  (r.status === 'pending_sp' && userRole === 'reseller' && r.sp_id === userId) ||
                  (r.status === 'pending_cp' && userRole === 'sub_reseller' && r.cp_id === userId)
                );
                const isExpanded = expandedId === r.id;
                const hasEmail   = r.email_hosting_requested || r.email_only;

                return (
                  <>
                    <tr
                      key={r.id}
                      className={`hover:bg-slate-50 cursor-pointer ${isExpanded ? 'bg-blue-50/30' : ''}`}
                      data-testid={`req-row-${r.id}`}
                      onClick={() => setExpandedId(isExpanded ? null : r.id)}
                    >
                      <td className="px-4 py-3 text-slate-400">
                        {isExpanded
                          ? <ChevronDown className="w-4 h-4 text-blue-500" />
                          : <ChevronRight className="w-4 h-4" />}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono font-semibold text-slate-800">{r.domain}</span>
                        {r.email_only && (
                          <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 font-semibold">EMAIL ONLY</span>
                        )}
                        {!r.email_only && hasEmail && (
                          <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold inline-flex items-center gap-0.5">
                            <Mail className="w-2.5 h-2.5" />+Email
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{r.requested_by_name || '—'}</td>
                      <td className="px-4 py-3 text-slate-600">{r.years}yr</td>
                      <td className="px-4 py-3 text-blue-700 font-semibold">
                        {r.email_only ? '—' : (() => {
                          const isQuoted = (r.quoted_total || 0) > 0;
                          const total = isQuoted
                            ? r.quoted_total
                            : (r.price || 0) + (r.email_cost_estimate || 0) + (r.domain_protector_cost || 0);
                          const hasExtras = (r.email_cost_estimate || 0) > 0 || (r.domain_protector_cost || 0) > 0;
                          return (
                            <span className="flex flex-col">
                              <span>{fmt(total)}</span>
                              {isQuoted ? (
                                <span className="text-[10px] text-violet-500 font-normal">incl. addons + GST</span>
                              ) : hasExtras && (
                                <span className="text-[10px] text-violet-500 font-normal">incl. addons</span>
                              )}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs">
                        {r.created_at ? new Date(r.created_at).toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${s.cls}`}>{s.label}</span>
                      </td>
                      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-2">
                          {/* Eye / View button — always visible */}
                          <button
                            data-testid={`view-req-${r.id}`}
                            onClick={() => setViewingRequest(r)}
                            className="p-1.5 rounded-lg border border-slate-200 text-slate-500 hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50 transition-colors"
                            title="View request details"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          {canApprove ? (
                            <>
                              <button
                                data-testid={`approve-req-${r.id}`}
                                disabled={approvingId === r.id}
                                onClick={() => handleApprove(r.id)}
                                className="px-3 py-1 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 disabled:opacity-50"
                              >
                                {approvingId === r.id ? '...' : 'Approve'}
                              </button>
                              <button
                                data-testid={`reject-req-${r.id}`}
                                onClick={() => onReject(r)}
                                className="px-3 py-1 border border-red-200 text-red-600 text-xs rounded-lg hover:bg-red-50"
                              >
                                Reject
                              </button>
                            </>
                          ) : null}
                        </div>
                      </td>
                    </tr>

                    {/* Expanded row with email details */}
                    {isExpanded && (
                      <tr key={`${r.id}-exp`} className="bg-blue-50/20">
                        <td colSpan={8} className="px-6 py-4">
                          <div className="flex flex-wrap gap-5">
                            {hasEmail ? (
                              <div className="flex-1 min-w-[200px]">
                                <p className="text-xs font-semibold text-violet-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                                  <Mail className="w-3.5 h-3.5" /> Email Hosting Details
                                </p>
                                <div className="bg-white rounded-xl border border-violet-100 px-4 py-3 space-y-1.5">
                                  <div className="flex items-center gap-2 text-sm">
                                    <span className="text-slate-500 text-xs">Accounts:</span>
                                    <span className="font-semibold text-slate-800">
                                      {r.email_accounts_count > 0 ? `${r.email_accounts_count} account(s)` : 'Not specified'}
                                    </span>
                                  </div>
                                  {r.email_usernames?.length > 0 && (
                                    <div>
                                      <p className="text-xs text-slate-500 mb-1">Usernames:</p>
                                      <div className="flex flex-wrap gap-1.5">
                                        {r.email_usernames.map((u, i) => (
                                          <span key={i} className="text-xs bg-violet-50 border border-violet-200 text-violet-700 rounded-full px-2.5 py-0.5 font-mono">
                                            {u}@{r.domain}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-slate-400 italic">No email hosting with this request.</p>
                            )}
                            {r.notes && (
                              <div className="flex-1 min-w-[180px]">
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Notes</p>
                                <div className="bg-white rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-600">{r.notes}</div>
                              </div>
                            )}
                            {r.status === 'rejected' && r.rejection_reason && (
                              <div className="flex-1 min-w-[180px]">
                                <p className="text-xs font-semibold text-red-500 uppercase tracking-wide mb-2">Rejection Reason</p>
                                <div className="bg-red-50 rounded-xl border border-red-100 px-4 py-3 text-sm text-red-700">{r.rejection_reason}</div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
